import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  FiX, FiClock, FiPlus, FiTrash2, FiAlertTriangle,
  FiLogIn, FiLogOut, FiPause, FiArrowRight,
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import api from '../../services/api'
import { Btn } from '../../ui'
import { formatearFecha } from '../../utils/fechas'
import { resumirOperacion } from '../../utils/correcciones'

/*
 * CorreccionDiaModal — Punto C.
 * Pantalla unificada de corrección a nivel de marcaje. Sustituye a los flujos
 * fragmentados (corregir-fichaje, corregir/borrar-pausa, pausa-nueva, olvido).
 *
 * Flujo: seleccionar fecha → ver el timeline de marcajes del día → editar
 * (modificar hora / eliminar turno o pausa / crear turno o pausa) → revisar el
 * resumen de cambios → motivo → enviar.
 *
 * Modos:
 *  - modo='empleado'  → POST /correcciones/dia (solicita al admin).
 *  - modo='admin'     → POST /correcciones/dia/proponer (propone al empleado;
 *                        requiere empleadoId).
 *
 * Props:
 *  - modo: 'empleado' | 'admin'
 *  - empleadoId?: number     (obligatorio en modo admin)
 *  - empleadoNombre?: string (solo display, modo admin)
 *  - fechaInicial?: 'YYYY-MM-DD'
 *  - onClose: () => void
 *  - onSuccess?: () => void
 */

// 'HH:MM:SS' | 'HH:MM' → 'HH:MM' (para <input type=time> y comparación).
const hhmm = (t) => (t ? String(t).slice(0, 5) : '')
// 'HH:MM' → 'HH:MM:SS' (para enviar al backend, formato canónico).
const hhmmss = (t) => {
  if (!t) return null
  const s = String(t)
  return s.length === 5 ? `${s}:00` : s
}
const horaASeg = (t) => {
  const m = hhmm(t).match(/^(\d{2}):(\d{2})$/)
  return m ? parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 : null
}

// Validación de coherencia en cliente (espejo ligero del backend, solo feedback).
// Devuelve { valido, error }.
const validarTimelineCliente = (turnos) => {
  const eventos = []
  turnos.forEach((t) => {
    if (t.hora_entrada) eventos.push({ m: 'entrada', s: horaASeg(t.hora_entrada) })
    ;(t.pausas || []).forEach((p) => {
      if (p.hora_inicio) eventos.push({ m: 'inicio_pausa', s: horaASeg(p.hora_inicio) })
      if (p.hora_fin) eventos.push({ m: 'fin_pausa', s: horaASeg(p.hora_fin) })
    })
    if (t.hora_salida) eventos.push({ m: 'salida', s: horaASeg(t.hora_salida) })
  })
  const peso = { fin_pausa: 0, entrada: 1, inicio_pausa: 2, salida: 3 }
  eventos.sort((a, b) => (a.s - b.s) || (peso[a.m] - peso[b.m]))
  const trans = {
    fuera: { entrada: 'dentro' },
    dentro: { inicio_pausa: 'pausado', salida: 'fuera' },
    pausado: { fin_pausa: 'dentro' },
  }
  const msg = {
    entrada: 'No se puede entrar: ya hay un turno abierto sin salida.',
    salida: 'No se puede salir: no hay ningún turno abierto.',
    inicio_pausa: 'No se puede iniciar pausa: no hay turno abierto o ya hay una pausa.',
    fin_pausa: 'No se puede finalizar pausa: no hay ninguna pausa en curso.',
  }
  let estado = 'fuera'
  let segPrev = -1
  for (const ev of eventos) {
    if (ev.s == null) return { valido: false, error: 'Hay una hora vacía o inválida.' }
    if (ev.s < segPrev) return { valido: false, error: 'Los marcajes no están en orden cronológico.' }
    const sig = trans[estado][ev.m]
    if (sig === undefined) return { valido: false, error: msg[ev.m] }
    estado = sig
    segPrev = ev.s
  }
  return { valido: true, error: null }
}

let TEMP_SEQ = -1
const nextTemp = () => TEMP_SEQ--

const CorreccionDiaModal = ({ modo = 'empleado', empleadoId, empleadoNombre, fechaInicial, onClose, onSuccess }) => {
  const hoy = new Date().toISOString().slice(0, 10)
  const [fecha, setFecha] = useState(fechaInicial || hoy)
  const [cargando, setCargando] = useState(false)
  const [tiposPausa, setTiposPausa] = useState([])
  const [original, setOriginal] = useState([]) // turnos tal como vienen del backend (normalizados a HH:MM)
  const [draft, setDraft] = useState([])        // copia editable
  const [motivo, setMotivo] = useState('')
  const [enviando, setEnviando] = useState(false)

  const esAdmin = modo === 'admin'

  const cargarMarcajes = useCallback(async () => {
    if (!fecha) return
    setCargando(true)
    try {
      const params = { fecha }
      if (esAdmin && empleadoId) params.empleado_id = empleadoId
      const { data } = await api.get('/correcciones/marcajes-dia', { params })
      const norm = (data.turnos || []).map((t) => ({
        fichaje_id: t.fichaje_id,
        hora_entrada: hhmm(t.hora_entrada),
        hora_salida: hhmm(t.hora_salida),
        pausas: (t.pausas || []).map((p) => ({
          pausa_id: p.pausa_id,
          tipo: p.tipo,
          hora_inicio: hhmm(p.hora_inicio),
          hora_fin: hhmm(p.hora_fin),
        })),
      }))
      setTiposPausa(data.tipos_pausa || [])
      setOriginal(norm)
      setDraft(JSON.parse(JSON.stringify(norm)))
    } catch (e) {
      toast.error(e.message || 'No se pudieron cargar los marcajes')
    } finally {
      setCargando(false)
    }
  }, [fecha, esAdmin, empleadoId])

  useEffect(() => { cargarMarcajes() }, [cargarMarcajes])

  // --- mutaciones del borrador ---
  const actualizar = (fn) => setDraft((prev) => { const c = JSON.parse(JSON.stringify(prev)); fn(c); return c })

  const setHoraTurno = (i, campo, val) => actualizar((d) => { d[i][campo] = val })
  const eliminarTurno = (i) => actualizar((d) => { d[i]._eliminado = true })
  const restaurarTurno = (i) => actualizar((d) => { delete d[i]._eliminado })
  const añadirTurno = () => actualizar((d) => { d.push({ fichaje_id: nextTemp(), hora_entrada: '', hora_salida: '', pausas: [], _nuevo: true }) })

  const setHoraPausa = (ti, pi, campo, val) => actualizar((d) => { d[ti].pausas[pi][campo] = val })
  const setTipoPausa = (ti, pi, val) => actualizar((d) => { d[ti].pausas[pi].tipo_pausa_id = val })
  const eliminarPausa = (ti, pi) => actualizar((d) => { d[ti].pausas[pi]._eliminado = true })
  const restaurarPausa = (ti, pi) => actualizar((d) => { delete d[ti].pausas[pi]._eliminado })
  const añadirPausa = (ti) => actualizar((d) => {
    const tp = tiposPausa[0]
    d[ti].pausas.push({ pausa_id: nextTemp(), tipo: tp?.codigo, tipo_pausa_id: tp?.id, hora_inicio: '', hora_fin: '', _nuevo: true })
  })

  // Proyección visible (sin los marcados como eliminados) para validar en cliente.
  const proyeccion = useMemo(() => draft
    .filter((t) => !t._eliminado)
    .map((t) => ({ ...t, pausas: (t.pausas || []).filter((p) => !p._eliminado) })),
  [draft])

  const coherencia = useMemo(() => validarTimelineCliente(proyeccion), [proyeccion])

  // Diff borrador → operaciones para el backend.
  const operaciones = useMemo(() => {
    const ops = []
    let orden = 0
    const origPorId = Object.fromEntries(original.map((t) => [t.fichaje_id, t]))

    draft.forEach((t) => {
      if (t._nuevo) {
        if (!t._eliminado) {
          ops.push({ accion: 'crear', objetivo: 'turno', valores_propuestos: { hora_entrada: hhmmss(t.hora_entrada), hora_salida: hhmmss(t.hora_salida) }, orden: orden++ })
          // pausas nuevas sobre un turno nuevo no se soportan en esta versión (sin id real)
        }
        return
      }
      const o = origPorId[t.fichaje_id]
      if (t._eliminado) {
        ops.push({ accion: 'eliminar', objetivo: 'turno', fichaje_id: t.fichaje_id, valores_originales: { hora_entrada: o?.hora_entrada, hora_salida: o?.hora_salida }, orden: orden++ })
        return
      }
      if (o && t.hora_entrada !== o.hora_entrada) {
        ops.push({ accion: 'modificar', objetivo: 'turno', marcaje: 'entrada', fichaje_id: t.fichaje_id, valores_originales: { hora_entrada: o.hora_entrada }, valores_propuestos: { hora_entrada: hhmmss(t.hora_entrada) }, orden: orden++ })
      }
      if (o && t.hora_salida !== o.hora_salida) {
        ops.push({ accion: 'modificar', objetivo: 'turno', marcaje: 'salida', fichaje_id: t.fichaje_id, valores_originales: { hora_salida: o.hora_salida }, valores_propuestos: { hora_salida: hhmmss(t.hora_salida) }, orden: orden++ })
      }
      const origPausas = Object.fromEntries((o?.pausas || []).map((p) => [p.pausa_id, p]))
      ;(t.pausas || []).forEach((p) => {
        if (p._nuevo) {
          if (!p._eliminado) ops.push({ accion: 'crear', objetivo: 'pausa', fichaje_id: t.fichaje_id, valores_propuestos: { hora_inicio: hhmmss(p.hora_inicio), hora_fin: hhmmss(p.hora_fin), tipo_pausa_id: p.tipo_pausa_id }, orden: orden++ })
          return
        }
        const op = origPausas[p.pausa_id]
        if (p._eliminado) {
          ops.push({ accion: 'eliminar', objetivo: 'pausa', pausa_id: p.pausa_id, valores_originales: { hora_inicio: op?.hora_inicio, hora_fin: op?.hora_fin }, orden: orden++ })
          return
        }
        if (op && p.hora_inicio !== op.hora_inicio) {
          ops.push({ accion: 'modificar', objetivo: 'pausa', marcaje: 'inicio_pausa', pausa_id: p.pausa_id, valores_originales: { hora_inicio: op.hora_inicio }, valores_propuestos: { hora_inicio: hhmmss(p.hora_inicio) }, orden: orden++ })
        }
        if (op && p.hora_fin !== op.hora_fin) {
          ops.push({ accion: 'modificar', objetivo: 'pausa', marcaje: 'fin_pausa', pausa_id: p.pausa_id, valores_originales: { hora_fin: op.hora_fin }, valores_propuestos: { hora_fin: hhmmss(p.hora_fin) }, orden: orden++ })
        }
      })
    })
    return ops
  }, [draft, original])

  const nombrePausa = (codigo) => tiposPausa.find((t) => t.codigo === codigo)?.nombre || codigo || 'Pausa'

  const enviar = async () => {
    if (operaciones.length === 0) { toast.error('No has hecho ningún cambio'); return }
    if (!motivo.trim()) { toast.error('Indica el motivo de la corrección'); return }
    if (!coherencia.valido && !window.confirm(`El resultado parece incoherente: ${coherencia.error}\n\n¿Enviar de todas formas? El administrador lo revisará.`)) return
    setEnviando(true)
    try {
      if (esAdmin) {
        await api.post('/correcciones/dia/proponer', { empleado_id: empleadoId, fecha, motivo, operaciones })
        toast.success('Propuesta enviada al empleado')
      } else {
        await api.post('/correcciones/dia', { fecha, motivo, operaciones })
        toast.success('Solicitud de corrección enviada')
      }
      onSuccess?.()
      onClose?.()
    } catch (e) {
      toast.error(e.message || 'No se pudo enviar la solicitud')
    } finally {
      setEnviando(false)
    }
  }

  const Reloj = ({ icon: Icon, label, value, onChange, tono = 'var(--ink-3)' }) => (
    <div className="flex items-center gap-2">
      <Icon size={13} style={{ color: tono }} />
      <span className="text-[11px] text-[var(--ink-3)] w-[58px]">{label}</span>
      <input type="time" value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] font-mono focus:outline-none focus:border-[var(--ink-3)]" />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
      <div className="ui-card w-full max-w-2xl max-h-[92vh] flex flex-col">
        {/* Cabecera */}
        <div className="flex items-start justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="eyebrow mb-1">{esAdmin ? 'Proponer corrección' : 'Solicitar corrección'}</div>
            <h3 className="font-display text-[20px] text-[var(--ink)] leading-tight">
              Marcajes del día{esAdmin && empleadoNombre ? ` · ${empleadoNombre}` : ''}
            </h3>
          </div>
          <button type="button" onClick={onClose} disabled={enviando}
            className="p-1.5 rounded-soft text-[var(--ink-3)] hover:text-[var(--ink)] hover:bg-[var(--bg-2)]" aria-label="Cerrar">
            <FiX size={16} />
          </button>
        </div>

        {/* Cuerpo */}
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className="eyebrow block mb-1.5">Fecha</label>
            <input type="date" value={fecha} max={hoy} onChange={(e) => setFecha(e.target.value)}
              className="px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] focus:outline-none focus:border-[var(--ink-3)]" />
            <p className="text-[11px] text-[var(--ink-3)] mt-1">{formatearFecha(fecha)}</p>
          </div>

          {cargando ? (
            <div className="py-8 text-center text-[13px] text-[var(--ink-3)]">Cargando marcajes…</div>
          ) : (
            <>
              {/* Timeline editable */}
              <div className="space-y-3">
                {draft.length === 0 && (
                  <div className="rounded-soft bg-[var(--bg-2)] p-4 text-[13px] text-[var(--ink-2)] text-center">
                    No hay marcajes registrados este día. Puedes crear un turno nuevo.
                  </div>
                )}
                {draft.map((t, ti) => (
                  <div key={t.fichaje_id} className={`rounded-soft border p-3 ${t._eliminado ? 'border-red-200 bg-red-50/40 opacity-70' : 'border-[var(--line)] bg-[var(--bg-1)]'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="eyebrow flex items-center gap-1.5">
                        Turno {ti + 1}{t._nuevo && <span className="text-emerald-600">· nuevo</span>}{t._eliminado && <span className="text-red-600">· se eliminará</span>}
                      </span>
                      {t._eliminado
                        ? <button type="button" onClick={() => restaurarTurno(ti)} className="text-[11px] text-[var(--ink-2)] hover:text-[var(--ink)] underline">Restaurar</button>
                        : <button type="button" onClick={() => eliminarTurno(ti)} className="text-[11px] text-red-500 hover:text-red-700 flex items-center gap-1"><FiTrash2 size={12} /> Eliminar turno</button>}
                    </div>
                    {!t._eliminado && (
                      <>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mb-2">
                          <Reloj icon={FiLogIn} label="Entrada" value={t.hora_entrada} onChange={(v) => setHoraTurno(ti, 'hora_entrada', v)} tono="#0a7d3f" />
                          <Reloj icon={FiLogOut} label="Salida" value={t.hora_salida} onChange={(v) => setHoraTurno(ti, 'hora_salida', v)} tono="#b3261e" />
                        </div>
                        {/* Pausas */}
                        <div className="pl-3 border-l-2 border-[var(--line)] space-y-2">
                          {(t.pausas || []).map((p, pi) => (
                            <div key={p.pausa_id} className={`flex flex-wrap items-center gap-x-4 gap-y-1.5 ${p._eliminado ? 'opacity-60' : ''}`}>
                              <FiPause size={12} className="text-[var(--ink-3)]" />
                              {p._nuevo || !p.tipo ? (
                                <select value={p.tipo_pausa_id || ''} onChange={(e) => setTipoPausa(ti, pi, Number(e.target.value))}
                                  className="px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] focus:outline-none">
                                  {tiposPausa.map((tp) => <option key={tp.id} value={tp.id}>{tp.nombre}</option>)}
                                </select>
                              ) : (
                                <span className="text-[12px] text-[var(--ink-2)] w-[70px]">{nombrePausa(p.tipo)}</span>
                              )}
                              <input type="time" value={p.hora_inicio || ''} onChange={(e) => setHoraPausa(ti, pi, 'hora_inicio', e.target.value)} disabled={p._eliminado}
                                className="px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] font-mono focus:outline-none" />
                              <FiArrowRight size={11} className="text-[var(--ink-3)]" />
                              <input type="time" value={p.hora_fin || ''} onChange={(e) => setHoraPausa(ti, pi, 'hora_fin', e.target.value)} disabled={p._eliminado}
                                className="px-2 py-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[12px] font-mono focus:outline-none" />
                              {p._eliminado
                                ? <button type="button" onClick={() => restaurarPausa(ti, pi)} className="text-[11px] underline text-[var(--ink-2)]">Restaurar</button>
                                : <button type="button" onClick={() => eliminarPausa(ti, pi)} className="text-red-400 hover:text-red-600"><FiTrash2 size={12} /></button>}
                            </div>
                          ))}
                          {!t._nuevo && (
                            <button type="button" onClick={() => añadirPausa(ti)} className="text-[11px] text-[var(--ink-2)] hover:text-[var(--ink)] flex items-center gap-1">
                              <FiPlus size={11} /> Añadir pausa
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
                <button type="button" onClick={añadirTurno}
                  className="w-full py-2 rounded-soft border border-dashed border-[var(--line)] text-[12px] text-[var(--ink-2)] hover:text-[var(--ink)] hover:border-[var(--ink-3)] flex items-center justify-center gap-1.5">
                  <FiPlus size={13} /> Crear turno nuevo
                </button>
              </div>

              {/* Coherencia */}
              {!coherencia.valido && operaciones.length > 0 && (
                <div className="rounded-soft bg-[rgba(168,106,6,.08)] border border-[rgba(168,106,6,.20)] p-3 text-[12px] text-[var(--ink-2)] flex items-start gap-2">
                  <FiAlertTriangle size={14} className="text-[#A86A06] mt-0.5 shrink-0" />
                  <span>{coherencia.error} Puedes enviarlo igualmente; el administrador lo revisará.</span>
                </div>
              )}

              {/* Resumen de cambios */}
              {operaciones.length > 0 && (
                <div className="rounded-soft bg-[var(--bg-2)] p-3">
                  <div className="eyebrow mb-2 flex items-center gap-1.5"><FiClock size={12} /> {operaciones.length} cambio(s)</div>
                  <ul className="space-y-1 text-[12px] text-[var(--ink-2)]">
                    {operaciones.map((op, i) => <li key={i} className="flex items-center gap-1.5"><FiArrowRight size={10} className="text-[var(--ink-3)]" /> {resumirOperacion(op)}</li>)}
                  </ul>
                </div>
              )}

              <div>
                <label className="eyebrow block mb-1.5">Motivo *</label>
                <textarea rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)}
                  placeholder="Explica brevemente el motivo de la corrección"
                  className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[14px] focus:outline-none focus:border-[var(--ink-3)] resize-none" />
              </div>
            </>
          )}
        </div>

        {/* Pie */}
        <div className="flex gap-3 p-5 border-t border-[var(--line)]">
          <Btn type="button" variant="ghost" className="flex-1" onClick={onClose} disabled={enviando}>Cancelar</Btn>
          <Btn type="button" variant="primary" className="flex-1" onClick={enviar} disabled={enviando || cargando || operaciones.length === 0}>
            {enviando ? 'Enviando…' : (esAdmin ? 'Enviar propuesta' : 'Enviar solicitud')}
          </Btn>
        </div>
      </div>
    </div>
  )
}

export default CorreccionDiaModal
